//Node Modules
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

//GraphQL
import { API, graphqlOperation } from "aws-amplify";
import { listDevicesAdminTable } from "../../../../graphql/queries-custom";

//Helpers
import { getAlarmString } from "../../../../helpers/Device";
import { getDevicePresence } from "../../../../helpers/DevicePresence";

//BinaryForge Components
import { PageHeader } from "../../../../components/layout";
import { Note } from "../../../../components/helpers";
import { AdminDevicesTable } from "../../../../components/devices";

//3rd Party Components
import { Button } from "primereact/button";

const PAGE_SIZE = 200;

const AdminDeviceList = () => {
	const { t } = useTranslation();
	const [devices, setDevices] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [nextToken, setNextToken] = useState(null);
	const [errorMessage, setErrorMessage] = useState(null);

	useEffect(() => {
		loadDevices();
	}, []);

	const loadDevices = async (token = null, append = false) => {
		if (append) {
			setLoadingMore(true);
		} else {
			setLoading(true);
			setErrorMessage(null);
		}

		try {
			const variables = { limit: PAGE_SIZE };
			if (token) {
				variables.nextToken = token;
			}

			const response = await API.graphql(graphqlOperation(listDevicesAdminTable, variables));
			const items = response?.data?.listDevices?.items || [];
			const newNextToken = response?.data?.listDevices?.nextToken || null;

			const formattedItems = items.map((device) => {
				let reportedState = null;
				if (device.reported_state) {
					try {
						reportedState = JSON.parse(device.reported_state);
					} catch (err) {
						console.warn("AdminDeviceList parse error ::", err);
					}
				}

				const hardwareVersion = reportedState?.hardware_version || null;
				const softwareVersion = reportedState?.firmware_version || reportedState?.software_version || null;
				let batteryPercentage = null;
				if (
					reportedState?.battery &&
					reportedState.battery.percentage !== undefined &&
					reportedState.battery.percentage !== null
				) {
					batteryPercentage = `${reportedState.battery.percentage}`;
				}

				const flattenSensorValues = (input) => {
					if (input === undefined || input === null) return [];
					if (Array.isArray(input)) return input.flatMap((value) => flattenSensorValues(value));
					if (typeof input === "object")
						return Object.values(input).flatMap((value) => flattenSensorValues(value));
					return [input];
				};

				const coerceSensorBinary = (value) => {
					if (value === undefined || value === null) return null;
					if (typeof value === "boolean") return value ? 1 : 0;
					if (typeof value === "number") return value ? 1 : 0;
					if (typeof value === "string") {
						const trimmed = value.trim().toLowerCase();
						if (!trimmed) return null;
						if (["1", "true", "leak", "alarm", "alert"].includes(trimmed)) return 1;
						if (["0", "false", "clear", "ok", "okay", "normal", "none"].includes(trimmed)) return 0;
					}
					return null;
				};

				const reportedSensorValues = [];
				reportedSensorValues.push(...flattenSensorValues(device.alert?.sensor_state));
				reportedSensorValues.push(...flattenSensorValues(reportedState?.sensor_state));
				reportedSensorValues.push(...flattenSensorValues(reportedState?.sensor));
				reportedSensorValues.push(...flattenSensorValues(reportedState?.sensors));
				reportedSensorValues.push(...flattenSensorValues(reportedState?.sensor1?.status));
				reportedSensorValues.push(...flattenSensorValues(reportedState?.sensor2?.status));

				const sensorBinaries = reportedSensorValues
					.map((value) => coerceSensorBinary(value))
					.filter((value) => value !== null);

				let sensorState = null;
				if (sensorBinaries.length > 0) {
					const leakDetected = sensorBinaries.some((value) => value === 1);
					sensorState = getAlarmString(leakDetected ? 1 : 0);
				} else {
					const stringValue = reportedSensorValues.find(
						(value) => typeof value === "string" && value.trim() !== "",
					);
					if (stringValue !== undefined) sensorState = stringValue;
				}

					const schemeName = device.scheme?.name || null;

					//Cross-scheme list, so alarm state comes from the alert record (the live atom is scheme-scoped)
					const inAlarm = Number(device.alert?.sensor_state) > 0;
					const online = !getDevicePresence({
						deviceId: device.id,
						reportedState: reportedState ?? {},
						fallbackUpdatedAt: device.updatedAt,
					}).isOffline;

					return {
							id: device.id,
							name: device.name,
							schemeId: device.schemeId,
							schemeName,
							schemeLabel: schemeName || device.schemeId,
					hardwareVersion,
					softwareVersion,
					batteryPercentage,
					sensorState,
					reported_state: device.reported_state,
					updatedAt: device.updatedAt,
					_online: online,
					_inAlarm: inAlarm,
				};
			});

			setDevices((prevDevices) => (append ? [...prevDevices, ...formattedItems] : formattedItems));
			setNextToken(newNextToken);
		} catch (err) {
			console.error("AdminDeviceList fetch error ::", err);
			setErrorMessage(t("admin.device.list.error.generic"));
		} finally {
			if (append) {
				setLoadingMore(false);
			} else {
				setLoading(false);
			}
		}
	};

	const handleReload = () => {
		loadDevices();
	};

	const handleLoadMore = () => {
		if (nextToken) {
			loadDevices(nextToken, true);
		}
	};

	return (
		<>
			<PageHeader title={t("admin.device.list.pageTitle")} subtitle={t("admin.device.list.pageSubtitle") || ""} />
			<main className="appMain">
				<div className="appMain-content">
					<div className="card">
						<Note
							icon="pi-info-circle fontColour-andel-blue2"
							messageKey={t("admin.device.list.description")}
							messageStyle="fontColour-lighter"
						/>
						<Note
							icon="pi-info-circle fontColour-andel-blue2"
							messageKey={t("admin.device.list.table.actions.notice")}
							messageStyle="fontColour-lighter"
							wrapperStyle="marginTop-small"
						/>
						{errorMessage && (
							<Note
								icon="pi-exclamation-circle fontColour-error"
								messageKey={errorMessage}
								messageStyle="fontColour-error"
								wrapperStyle="marginTop-small"
							/>
						)}
						<div className="flex jContent-between aItems-center marginBlock-small">
							<span className="fontWeight-bold">
								{t("admin.device.list.summary", { count: devices.length })}
							</span>
							{loading && !loadingMore && (
								<span className="fontColour-lighter">{t("admin.device.list.loading")}</span>
							)}
						</div>
						<AdminDevicesTable devices={devices} loading={loading || loadingMore} />
						<div className="flex gap-small jContent-end marginTop-small">
							<Button
								type="button"
								label={t("admin.device.list.actions.refresh")}
								icon="pi pi-refresh"
								onClick={handleReload}
								loading={loading && !loadingMore}
								disabled={loading && !loadingMore}
							/>
							{nextToken && (
								<Button
									type="button"
									label={t("admin.device.list.actions.loadMore")}
									icon="pi pi-download"
									onClick={handleLoadMore}
									loading={loadingMore}
									disabled={loadingMore}
								/>
							)}
						</div>
						{errorMessage && (
							<div className="flex marginTop-small">
								<Button
									type="button"
									label={t("admin.device.list.actions.retry")}
									icon="pi pi-refresh"
									onClick={handleReload}
									disabled={loading}
								/>
							</div>
						)}
					</div>
				</div>
			</main>
		</>
	);
};

export default AdminDeviceList;
